import { useState, useEffect } from 'react';
import { sendCreatePermanentTokenRequest } from './api/accountApi';

// eslint-disable-next-line no-magic-numbers -- clearly explained science, not magic
const TOKEN_TIMEOUT_MS = 4.9 * 60 * 1000; // ~ almost 5 minutes in milliseconds

let inflightRequest: Promise<void> | null = null;
let refreshTimeout: ReturnType<typeof setTimeout> | undefined;
const tokenSubscribers = new Set<(token: string) => void>();

const requestDownloadToken = (): Promise<void> => {
  if (inflightRequest) {
    return inflightRequest;
  }

  inflightRequest = sendCreatePermanentTokenRequest()
    .then((token) => {
      tokenSubscribers.forEach((cb) => {
        cb(token);
      });
      if (refreshTimeout) {
        clearTimeout(refreshTimeout);
      }
      refreshTimeout = setTimeout(requestDownloadToken, TOKEN_TIMEOUT_MS);
    })
    .catch((error: unknown) => {
      console.error('Fetching /perm-token failed', error);
    })
    .finally(() => {
      inflightRequest = null;
    });

  return inflightRequest;
};

/**
 * React hook to manage state and refreshes of `perm_token` attached to personalized download links.
 *
 * The token is strictly single use,
 * so if you have a way of knowing that it has been used (i.e. user has clicked a download link with it),
 * you should refresh it with `refreshDownloadToken()`.
 */
const useDownloadToken = () => {
  const [downloadToken, setDownloadToken] = useState('');

  useEffect(() => {
    tokenSubscribers.add(setDownloadToken);

    return () => {
      tokenSubscribers.delete(setDownloadToken);
    };
  }, []);

  return {
    downloadToken,
    refreshDownloadToken: requestDownloadToken,
  };
};

export { useDownloadToken };
